import { GridFilterItem, GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';

interface IUseServerSideOptions {
  initialSortModel?: GridSortModel;
  defaultSortModel?: GridSortModel;
}

interface IFilters {
  propertyName?: string;
  propertyValue?: string;
  operation?: number;
  timeZoneId?: string;
}
  
interface IOperationOnTable {
  filters?: IFilters[];
  orderBy?: string;
  orderByDirection?: number;
  pageNumber?: number;
  operator?: number;
}

export enum OrderByDirectionEnum {
  asc,
  desc
}

enum OperationEnum {
  equals,
  greaterThan,
  greaterThanOrEqual,
  lessThan,
  lessThanOrEqual,
  contains
}

enum FilterConditionEnum {
  or,
  and
}

const operators = {
  '=': 'equals',
  '<': 'lessThan',
  '>': 'greaterThan',
  '<=': 'lessThanOrEqual',
  '>=': 'greaterThanOrEqual',
  is: 'equals',
  after: 'greaterThan',
  onOrAfter: 'greaterThanOrEqual',
  before: 'lessThan',
  onOrBefore: 'lessThanOrEqual'
};

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const DEFAULT_TIME_ZONE = 'America/Denver';

export const useServerSide = ({
  initialSortModel = [],
  defaultSortModel = [],
}: IUseServerSideOptions = {}) => {
  //the sort to restore on clear
  const defaultSortModelRef = useRef<GridSortModel>(defaultSortModel);

  const [sortModel, setSortModel] = useState<GridSortModel>(
    initialSortModel.length ? initialSortModel : defaultSortModel
  );

  const [requestPayload, setRequestPayload] = useState<IOperationOnTable>(() => {
    const initialSort = initialSortModel[0];
  
    return {
      pageNumber: 1,
      orderBy: initialSort?.field,
      orderByDirection: initialSort?.sort
        ? OrderByDirectionEnum[
            initialSort.sort as keyof typeof OrderByDirectionEnum
          ]
        : undefined,
    };
  });
  
  const [filterData, setFilterData] = useState<GridFilterModel>({ items: [] });

  const setFilterDataMethod = useCallback((localFilterData: GridFilterModel) => {
    const hasValue = localFilterData.items.find((data: GridFilterItem) => data.value);
    const selectedFilters = localFilterData.items.filter((data) => data.value) || [];
    const selectedLinkOperator = localFilterData?.linkOperator
        ? (localFilterData?.linkOperator as keyof typeof FilterConditionEnum)
        : FilterConditionEnum[1];

      if (hasValue || requestPayload.filters) {
        const logicalOperator =
          selectedFilters.length > 1 ? FilterConditionEnum[selectedLinkOperator as keyof typeof FilterConditionEnum] : undefined;
      setRequestPayload((prev) => ({
        ...prev,
        operator: logicalOperator,
        filters: hasValue ? selectedFilters.map((data: GridFilterItem) => {
          let value = data.value;

      const isDateValue = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

      if (isDateValue) {
        value = format(parseISO(value), 'MM/dd/yyyy');
      }
          return {
          propertyName: data.columnField,
          propertyValue: value,
          operation: data?.operatorValue
            ? OperationEnum[(operators[data.operatorValue as keyof typeof operators] || data.operatorValue) as keyof typeof OperationEnum]
            : undefined,
          ...(isDateValue && { timeZoneId: userTimeZone || DEFAULT_TIME_ZONE})
        };
      }) : undefined
      }));
    }
  }, [requestPayload.filters]);

  const handleClosePanel = useCallback(() => {
    setFilterDataMethod(filterData);
  }, [filterData, setFilterDataMethod]);

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      const defaultSort = defaultSortModelRef.current[0];

      let nextModel = model;

      //when model becomes [], restore the default sort
      if (model.length === 0 && defaultSort) {
        nextModel = [defaultSort];
      }

      setSortModel(nextModel);

      setRequestPayload((prev) => {
        const newPayload = { ...prev };

        if (nextModel.length > 0) {
          const sort = nextModel[0];

          newPayload.orderBy = sort.field;
          newPayload.orderByDirection = sort.sort
            ? OrderByDirectionEnum[
                sort.sort as keyof typeof OrderByDirectionEnum
              ]
            : undefined;
        } else {
          delete newPayload.orderBy;
          delete newPayload.orderByDirection;
        }

        return newPayload;
      });
    },
    []
  );

  const onFilterModelChange = useCallback((filterModel: GridFilterModel) => {
    setFilterData(filterModel);
    setFilterDataMethod(filterModel);
  }, [setFilterDataMethod]);

  const onPageChange = useCallback((page: number) => {
    setRequestPayload((prev) => ({
      ...prev,
      pageNumber: page + 1
      // As Mui data grid has 0 as default page number,
      // So need to increment by one as API need page number starts from 1
    }));
  }, []);

  return {
    sortModel,
    requestPayload,
    onSortModelChange,
    onFilterModelChange,
    onPageChange,
    handleClosePanel
  };
};
